import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { AxiosResponse } from 'axios'
import { DownloadArchiveResponse, VirtualVisitDTO } from 'models/visuals'
import { JobStatus, RoleMimetype } from 'constants/API'
import { QueryClient, useQuery } from '@tanstack/react-query'
import { StagingRequestDTO } from 'models/virtualStaging'
import { StatusResponse } from 'models/redux'
import { VisualFileType } from 'constants/visual'
import { VisualListResponse } from 'models/visuals'
import { _invalidateOrder } from './order.query'
import { IdentifiableProduct } from 'utils/serialization/productIdentifier'
import { JobDTO } from 'models/misc'
import { buildVirtualVisitExtensionParams } from 'utils/serialization/upsellPayloads'
import { useAPI } from 'utils/API'

enum Endpoints {
  LIST_VISUALS = '/assignment/{assignmentId}/visual/all?type={type}',
  LIST_VISUALS_WITH_WORKSPACE = '/workspace/{workspaceId}/assignment/{assignmentId}/visual/all?type={type}',
  SELECT_VISUALS_FOR_STAGING = '/workspace/{workspaceId}/assignment/{assignmentId}/visual/staging/select',
  EXTEND_VIRTUAL_VISIT = '/workspace/{workspaceId}/assignment/{assignmentId}/virtualVisit/extendExpiration',
  ARCHIVE_VISUALS = '/assignment/{assignmentId}/visual/archive',
  ARCHIVE_EDITING_VISUALS = '/assignment/{assignmentId}/editedVisuals/{exportId}/archive',
  DOWNLOAD_ARCHIVE = '/assignment/{assignmentId}/visual/archive/{jobId}',
  JOB = '/job/{id}',
}

export const invalidateListVisuals = (client: QueryClient, assignmentId: string) => {
  client.invalidateQueries({ queryKey: [EntityKeys.VISUAL, QueryType.LIST, { assignmentId }] })
}

export function useListVisuals(assignmentId: string, type: VisualFileType, workspaceId?: string) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.VISUAL, QueryType.LIST, { assignmentId, type, workspaceId }],
    queryFn: () => {
      if (workspaceId) {
        return api.get<VisualListResponse>(
          Endpoints.LIST_VISUALS_WITH_WORKSPACE,
          { workspaceId, assignmentId, type },
          false
        )
      }
      return api.get<VisualListResponse>(
        Endpoints.LIST_VISUALS,
        { assignmentId, type },
        false
      )
    },
    enabled: !!assignmentId,
  })
}

// MUTATIONS

interface SelectVisualsForStagingPayload {
  assignmentId: string
  dealId: number
  workspaceId: string
  stagingAssignmentId: string
  stagingConfiguration: StagingRequestDTO
}

interface ExtendVirtualVisitPayload {
  assignmentId: string
  workspaceId: string
  paymentIntentId?: string
  /** The payment option the client picked — passed whole so it can be identified by its Product2 code. */
  billingProduct: IdentifiableProduct
}

export function useExtendVirtualVisitHosting() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<VirtualVisitDTO>, ExtendVirtualVisitPayload>(
    ({ assignmentId, workspaceId, paymentIntentId, billingProduct }) => api.put(
      Endpoints.EXTEND_VIRTUAL_VISIT,
      { assignmentId, workspaceId },
      {},
      true,
      { params: buildVirtualVisitExtensionParams({ billingProduct, paymentIntentId, correlation: { assignmentId } }) }
    ),
    (client, { assignmentId }) => {
      invalidateListVisuals(client, assignmentId)
    }
  )
}

// ARCHIVE & DOWNLOAD

interface ArchiveVisualsPayload {
  assignmentId: string
  types: string[]
  filenames: string[]
  isAdmin?: boolean
}

export function useArchiveVisuals() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<JobDTO>, ArchiveVisualsPayload>(
    ({ assignmentId, types, filenames, isAdmin = false }) => api.post(
      Endpoints.ARCHIVE_VISUALS,
      { assignmentId },
      { types, filenames },
      true,
      { headers: { Accept: isAdmin ? RoleMimetype.ADMIN : RoleMimetype.CLIENT } }
    )
  )
}

const POLL_INTERVAL = 5000
const MAX_POLL_COUNT = 120

/**
 * Polls the job endpoint until finished, then fetches the archive download URL.
 */
export function useDownloadArchive(assignmentId: string, jobId: string | null, isAdmin = false) {
  const api = useAPI<Endpoints>()

  return useQuery<DownloadArchiveResponse | null>({
    queryKey: [EntityKeys.VISUAL, 'archive', { assignmentId, jobId }],
    queryFn: async () => {
      const jobResponse = await api.get<JobDTO>(Endpoints.JOB, { id: jobId! }, true)

      if (jobResponse.data.status === JobStatus.FAILED) {
        throw new Error(jobResponse.data.error || 'Archive job failed')
      }

      if (jobResponse.data.status !== JobStatus.FINISHED) {
        return null
      }

      const downloadResponse = await api.get<DownloadArchiveResponse>(
        Endpoints.DOWNLOAD_ARCHIVE,
        { assignmentId, jobId: jobId! },
        true,
        { headers: { Accept: isAdmin ? RoleMimetype.ADMIN : RoleMimetype.CLIENT } }
      )

      return downloadResponse.data
    },
    enabled: !!jobId && !!assignmentId,
    refetchInterval: (query) => {
      if (query.state.data?.url) return false
      if (query.state.error) return false
      if (query.state.dataUpdateCount >= MAX_POLL_COUNT) return false
      return POLL_INTERVAL
    },
    retry: false,
  })
}

// STAGING & OTHER MUTATIONS

export function useSelectVisualsForStaging() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, SelectVisualsForStagingPayload>(
    ({ assignmentId, workspaceId, stagingAssignmentId, stagingConfiguration }) => api.post(
      Endpoints.SELECT_VISUALS_FOR_STAGING,
      { assignmentId, workspaceId },
      { ...stagingConfiguration },
      true,
      {
        params: { stagingAssignmentId },
        headers: { Accept: RoleMimetype.CLIENT },
      }
    ),
    (client, { dealId }) => {
      _invalidateOrder(client, dealId)
    }
  )
}
